import { memo, useEffect, useRef, useState } from "react";
import { DISCOVERY_CALL_MINUTES } from "@/lib/discovery-call";
import { motion } from "framer-motion";
import { CalendarDays, ExternalLink, Clock, Phone } from "lucide-react";
import logoIcon from "@/assets/logo-icon.webp";
import {
  GHL_BOOKING_IFRAME_ID_PREFIX,
  GHL_BOOKING_URL,
} from "@/lib/booking";
import { embedsAllowed, onConsentChange } from "@/lib/consent";
import { loadBookingResizer } from "@/lib/thirdParty";
import { useBookingDisplay } from "@/lib/bookingDisplay";
import { SITE_IDENTITY } from "@/lib/site-identity";

const fade = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true, margin: "-50px" as const },
};

const visibleBookingEmbeds = new Set<HTMLElement>();

function syncBookingVisibility() {
  document.body.classList.toggle(
    "booking-embed-visible",
    visibleBookingEmbeds.size > 0,
  );
}

type BrandedSchedulerProps = {
  instanceId?: string;
  /**
   * 2026-09-08: pressing "Book a demo" and then being shown a paragraph and a
   * second button was costing bookings, so a visitor who reached this page
   * from somewhere else on chleb.ai has already asked for the calendar and it
   * opens for them. The consent notice still stands for a cold arrival (a
   * search result, an ad, a pasted link), and this never touches the stored
   * cookie choice or the chat widget: it is the same per-visit scope the
   * button below already had.
   */
  intentAutoLoad?: boolean;
};

const arrivedFromThisSite = () => {
  if (typeof document === "undefined") return false;
  const ref = document.referrer;
  if (!ref) return false;
  try {
    return new URL(ref).origin === window.location.origin;
  } catch {
    return false;
  }
};

export const BrandedScheduler = memo(
  ({ instanceId = "inline", intentAutoLoad = false }: BrandedSchedulerProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    // The calendar is a HighLevel page inside an iframe. It sets its own
    // cookies and, from inside that frame, requests files from Google and
    // Meta. So it does not load until either a stored choice allows embeds or
    // the visitor asks for it here. "Ask for it here" is scoped to this page
    // view on purpose: it starts the calendar the visitor wants without
    // quietly turning on the chat widget as well.
    const [consented, setConsented] = useState(false);
    const [askedHere, setAskedHere] = useState(false);
    const show = consented || askedHere;
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const bookingDisplay = useBookingDisplay(iframeRef, show);

    useEffect(() => {
      setConsented(embedsAllowed());
      return onConsentChange((record) => setConsented(record.embeds));
    }, []);

    useEffect(() => {
      if (intentAutoLoad && arrivedFromThisSite()) setAskedHere(true);
    }, [intentAutoLoad]);

    useEffect(() => {
      // HighLevel's resizer only exists to size this iframe, so it loads with
      // the iframe and never on a page that has no calendar on it.
      if (show) loadBookingResizer();
    }, [show]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) visibleBookingEmbeds.add(container);
        else visibleBookingEmbeds.delete(container);
        syncBookingVisibility();
      }, { threshold: 0.02 });

      observer.observe(container);
      return () => {
        observer.disconnect();
        visibleBookingEmbeds.delete(container);
        syncBookingVisibility();
      };
    }, []);

    return (
      <motion.div
        ref={containerRef}
        {...fade}
        transition={{ duration: 0.7, delay: 0.15 }}
        className="self-stretch overflow-hidden rounded-3xl border border-black/[0.08] bg-white shadow-[0_10px_40px_-12px_rgba(0,0,0,0.15)]"
      >
        <div className="flex items-center gap-3 border-b border-black/[0.06] px-5 py-4 sm:px-6">
          <img
            src={logoIcon}
            alt="Chleb AI logo"
            width={40}
            height={40}
            className="h-10 w-10 object-contain"
          />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#6e6e73]">
              Chleb AI
            </p>
            <p className="text-sm font-bold text-[#1D1D1F]">Discovery Call</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#1D1D1F]/[0.08] bg-[#1D1D1F]/[0.04] px-2.5 py-1">
            <Clock size={11} className="text-[#1D1D1F]" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#1D1D1F]">
              {DISCOVERY_CALL_MINUTES} min
            </span>
          </span>
        </div>

        {show ? (
          <iframe
            ref={iframeRef}
            src={GHL_BOOKING_URL}
            id={`${GHL_BOOKING_IFRAME_ID_PREFIX}_${instanceId}`}
            title="Book a Chleb AI discovery call"
            className="block min-h-[900px] w-full border-0 bg-white"
            loading="eager"
          />
        ) : (
          <div className="flex min-h-[420px] flex-col items-center justify-center gap-4 px-6 py-12 text-center">
            <p className="max-w-md text-sm text-[#1D1D1F]">
              Our booking calendar is hosted by HighLevel. Opening it here lets
              HighLevel store cookies and data in your browser, and the page it
              serves inside this frame also requests files from Google and Meta.
            </p>
            <button
              type="button"
              onClick={() => setAskedHere(true)}
              className="min-h-[44px] rounded-full bg-[#1D1D1F] px-6 py-2 text-sm font-semibold text-white transition hover:bg-black"
            >
              Load the booking calendar
            </button>
            <p className="max-w-md text-xs text-[#6e6e73]">
              This loads the calendar for this visit only. It does not change
              your cookie choice.{" "}
              <a href="/cookie-policy" className="underline underline-offset-2">
                What it stores
              </a>
              .
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-semibold text-[#6e6e73]">
              <a
                href={SITE_IDENTITY.phone.tel}
                className="inline-flex items-center gap-1.5 underline"
              >
                <Phone size={12} /> {SITE_IDENTITY.phone.display}
              </a>
              <a
                href={GHL_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 underline"
              >
                Open the calendar in a new tab <ExternalLink size={12} />
              </a>
            </div>
          </div>
        )}

        {show && bookingDisplay === "blocked" && (
          <div
            role="alert"
            className="border-t border-amber-200 bg-amber-50 px-5 py-4 text-center text-sm text-amber-950"
          >
            <p className="font-semibold">The booking calendar is not visible right now.</p>
            <p className="mt-1 text-xs">Open it directly or call us and we will help you find a time.</p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-semibold">
              <a
                href={GHL_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 underline"
              >
                Open booking calendar in a new tab <ExternalLink size={12} />
              </a>
              <a href={SITE_IDENTITY.phone.tel} className="inline-flex items-center gap-1.5 underline">
                <Phone size={12} /> Call {SITE_IDENTITY.phone.display}
              </a>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 border-t border-black/[0.06] px-5 py-3 text-[11px] font-semibold text-[#6e6e73]">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays size={12} /> Booking calendar
          </span>
          <a href={GHL_BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 underline">
            Open booking calendar <ExternalLink size={12} />
          </a>
        </div>
        <p className="px-5 pb-4 text-center text-xs text-[#6e6e73]">Your time is reserved only after the calendar confirms your booking.</p>
      </motion.div>
    );
  },
);

BrandedScheduler.displayName = "BrandedScheduler";
